/**
 * Anonymous player ID management
 * Generates and stores a unique UUID per browser/device
 * Uses UUID v4 for collision resistance in global leaderboard
 */

const PLAYER_ID_KEY = 'letterweave_player_id';
const PLAYER_NAME_KEY = 'letterweave_player_name';

/**
 * Generate a UUID v4 (random UUID)
 * This provides much better collision resistance than timestamp-based IDs
 * Collision probability is extremely low (1 in 5.3 × 10^36 for UUID v4)
 */
function generateUUID(): string {
  // UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  // where x is any hexadecimal digit and y is one of 8, 9, A, or B
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Generate a unique anonymous ID (UUID v4)
 */
function generatePlayerId(): string {
  return generateUUID();
}

/**
 * Check if a player ID is in the old format (needs migration)
 */
function isOldFormat(playerId: string): boolean {
  // Old format: player_<timestamp>_<random>
  // New format: UUID v4 (xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return !uuidRegex.test(playerId) && /^player_\d+_[a-z0-9]+$/i.test(playerId);
}

/**
 * Get or create a player ID for this browser/device
 * Stores the ID in localStorage for persistence
 * Automatically migrates old format IDs to UUID v4
 */
export function getPlayerId(): string {
  if (typeof window === 'undefined') {
    return generatePlayerId();
  }

  try {
    let playerId = window.localStorage.getItem(PLAYER_ID_KEY);
    
    // Migrate old format IDs to UUID v4
    if (playerId && isOldFormat(playerId)) {
      console.log('Migrating old player ID format to UUID v4');
      playerId = generatePlayerId();
      window.localStorage.setItem(PLAYER_ID_KEY, playerId);
    }
    
    if (!playerId) {
      playerId = generatePlayerId();
      window.localStorage.setItem(PLAYER_ID_KEY, playerId);
    }
    
    return playerId;
  } catch (error) {
    // If localStorage fails, generate a new ID (won't persist)
    console.warn('Failed to access localStorage for player ID:', error);
    return generatePlayerId();
  }
}

/**
 * Get or set an optional display name for the player
 * If not set, returns a default name
 */
export function getPlayerName(): string {
  if (typeof window === 'undefined') {
    return 'Player';
  }

  try {
    const storedName = window.localStorage.getItem(PLAYER_NAME_KEY);
    if (storedName) {
      return storedName;
    }
    
    // If no name set, return default
    return 'Player';
  } catch (error) {
    console.warn('Failed to access localStorage for player name:', error);
    return 'Player';
  }
}

/**
 * Set a custom display name for the player
 */
export function setPlayerName(name: string): void {
  if (typeof window === 'undefined') return;
  
  try {
    const trimmedName = name.trim().substring(0, 20); // Limit to 20 chars
    if (trimmedName) {
      window.localStorage.setItem(PLAYER_NAME_KEY, trimmedName);
    }
  } catch (error) {
    console.warn('Failed to save player name:', error);
  }
}

/**
 * Reset player ID (generates a new one)
 * Useful if user wants to start fresh
 */
export function resetPlayerId(): string {
  const newId = generatePlayerId();
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(PLAYER_ID_KEY, newId);
    } catch (error) {
      console.warn('Failed to reset player ID:', error);
    }
  }
  return newId;
}

